import { uploadFile } from 'api/v2/upload';
import axios from 'axios';
import useCustomDropzone from 'hooks/useCustomDropzone';
import { useKeyValueStateControlled } from 'hooks/useKeyValueState';
import { omit, uniqBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { ErrorCode } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { UploadTypesEnum } from 'types/uploadEnums.types';
import { getTracingEvents } from 'utils/amplitude';
import { emailAcceptedFileUploaderTypes } from 'utils/constants';
import { ONE_MB } from 'utils/fileSizes';
import { extractResponseErrors } from 'utils/helpers';
import showToast from 'utils/toast';
import {
  WEBAPP_SENDEMAIL_ATTACHEDFILE,
  WEBAPP_SENDEMAIL_ATTACHEDTOOMANY,
} from './sendEmailAmplitude';
import { FileState } from './utils';

const events = getTracingEvents({
  onUploadSuccess: {
    name: WEBAPP_SENDEMAIL_ATTACHEDFILE,
    data: (type: string, size: number) => ({
      success: true,
      type: type?.split('/').pop(),
      size: size / 1000,
    }),
  },
  onUploadError: {
    name: WEBAPP_SENDEMAIL_ATTACHEDFILE,
    data: (
      type: string,
      size: number,
      reason: unknown,
      isBackendError?: boolean,
    ) => ({
      success: false,
      type: type?.split('/').pop(),
      size: size / 1000,
      reason,
      isBackendError: !!isBackendError,
    }),
  },
  onTooManyFiles: {
    name: WEBAPP_SENDEMAIL_ATTACHEDTOOMANY,
    data: (max: number, attemped: number) => ({
      max,
      attemped,
    }),
  },
});

const displayToastWarning = (message: string) => {
  showToast({ title: message }, { type: 'warning' });
};

type UseExternalFilesObjectProps = {
  getFieldValue: () => Record<string, FileState>;
  setFieldValue: (value: Record<string, FileState>) => void;
};

const useExternalFilesObject = (props: UseExternalFilesObjectProps) => {
  const { t } = useTranslation();

  const filesUID = useRef(0);

  const { setFieldValue } = props;

  const getFieldValue = () => props.getFieldValue() || {};

  const [innerAttachmentsObject, setInnerAttachmentsObject] =
    useState(getFieldValue);

  const filesAPI = useKeyValueStateControlled<FileState>({
    controller: {
      state: innerAttachmentsObject,
      setState: (updater) => {
        setInnerAttachmentsObject(updater);
        const updated = updater(getFieldValue());
        const simpleFormValue = Object.fromEntries(
          Object.entries(updated).map(([key, value]) => [
            key,
            omit(value, ['file', 'cancel']),
          ]),
        );
        setFieldValue(simpleFormValue);
      },
    },
  });

  const innerAttachmentsObjectRef = useRef(innerAttachmentsObject);
  innerAttachmentsObjectRef.current = innerAttachmentsObject;
  useEffect(() => {
    // cleanup uploading files on unmount
    return () => {
      Object.values(innerAttachmentsObjectRef.current).forEach((state) => {
        state.cancel?.();
      });
    };
  }, []);

  const maxFilesNumber = 2;
  const validFiles = filesAPI.values.filter((state) => !state.isError);
  const uploadSlots = Math.max(maxFilesNumber - validFiles.length, 0);
  const hasOpenUploadSlots = uploadSlots > 0;

  const isUploading = filesAPI.values.some((state) => state.isLoading);

  const processFile = (file: File, stateId?: string) => {
    const source = axios.CancelToken.source();
    const state = {
      key: stateId ?? String('_' + filesUID.current++),
      file: file,
      name: file.name,
      type: file.type.split('/').pop(),
      progress: 0,
      isLoading: true,
      isError: false,
      cancel: () => source.cancel(),
    };
    filesAPI.set(state.key, state);
    uploadFile({
      file: file,
      category: UploadTypesEnum.emailAttachment,
      cancelToken: source.token,
      onProgress: (progress) => filesAPI.update(state.key, { progress }),
    })
      .then((fields) => {
        filesAPI.update(state.key, {
          isLoading: false,
          isError: false,
          progress: 100,
          path: fields.key,
        });
        events.onUploadSuccess(file.type, file.size);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        const errors = extractResponseErrors(error);
        const canRetry = errors.every(
          (code) => code !== 'file-format-not-supported',
        );
        filesAPI.update(state.key, {
          isLoading: false,
          isError: true,
          canRetry,
        });
        events.onUploadError(
          file.type,
          file.size,
          errors.join(', ') || 'unknown',
          true,
        );
      });
  };

  const uploaderAPI = useCustomDropzone({
    maxSize: ONE_MB * 3,
    multiple: true,
    accept: emailAcceptedFileUploaderTypes,
    onDropRejected: (rejections) => {
      rejections.forEach((rejection) => {
        const file = rejection.file;
        const errors = rejection.errors.map((error) => error.code).join(', ');
        events.onUploadError(file.type, file.size, errors);
      });

      const rejectionsFormatted = rejections.flatMap((rejection) =>
        rejection.errors.map((error) => ({
          code: error.code,
          fileType:
            error.code === ErrorCode.FileInvalidType
              ? rejection.file.type
              : 'N/A',
        })),
      );
      const errors = uniqBy(
        rejectionsFormatted,
        (error) => `${error.code}-${error.fileType}`,
      );
      errors.forEach((error) => {
        if (error.code === ErrorCode.FileTooLarge) {
          displayToastWarning(t('email_client.errors.file_too_large'));
        }
        if (error.code === ErrorCode.FileInvalidType) {
          displayToastWarning(
            t('email_client.errors.invalid_file_type', {
              fileType: error.fileType.split('/').pop(),
            }),
          );
        }
      });
    },
    onDropAccepted: (files) => {
      files.slice(0, uploadSlots).forEach((file) => processFile(file));
      if (files.length > uploadSlots) {
        events.onTooManyFiles(
          maxFilesNumber,
          files.length + (maxFilesNumber - uploadSlots),
        );
        displayToastWarning(
          t('email_client.errors.too_many_files', {
            maxFiles: maxFilesNumber,
          }),
        );
      }
    },
  });

  return {
    uploaderAPI,
    filesAPI,
    processFile,
    hasOpenUploadSlots,
    isUploading,
  };
};

export default useExternalFilesObject;
